import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, SkipForward } from 'lucide-react';

import { Keycap } from '../common/index.jsx';

/**
 * Always-visible image navigation, in the sticky top bar.
 *
 * Previously the only controls were at the very bottom of a long page, so moving to the
 * next image meant scrolling past the span list, the final preview and the admin
 * panels. Position, prev/next and a jump list now stay on screen the whole time.
 */
export function TaskNavigator({ position, total, items, currentTaskId, onGo, onNextUnfinished, hasPrev, hasNext }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onAway = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onAway);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onAway);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div className="task-nav" ref={ref}>
      <button
        type="button"
        className="btn sm icon"
        disabled={!hasPrev}
        onClick={() => onGo('prev')}
        title="Previous image (left arrow)"
        aria-label="Previous image"
      >
        <ChevronLeft size={15} />
      </button>

      <button
        type="button"
        className="task-nav-position"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title="Jump to an image"
      >
        {position ? (
          <>
            <strong>{position}</strong>
            <span className="faint"> / {total}</span>
          </>
        ) : (
          <span className="faint">image</span>
        )}
        <ChevronDown size={13} />
      </button>

      <button
        type="button"
        className="btn sm icon"
        disabled={!hasNext}
        onClick={() => onGo('next')}
        title="Next image (right arrow)"
        aria-label="Next image"
      >
        <ChevronRight size={15} />
      </button>

      <button
        type="button"
        className="btn sm"
        onClick={onNextUnfinished}
        title="Jump to the next image that still needs work"
      >
        <SkipForward size={13} /> Next unfinished
      </button>

      {open && (
        <div className="task-nav-menu" role="listbox">
          {items.length === 0 ? (
            <div className="task-nav-empty">Nothing to jump to.</div>
          ) : (
            items.map((item, i) => (
              <button
                key={item.task_id}
                type="button"
                role="option"
                aria-selected={item.task_id === currentTaskId}
                className={`task-nav-item ${item.task_id === currentTaskId ? 'current' : ''}`}
                onClick={() => {
                  setOpen(false);
                  onGo(item.task_id);
                }}
              >
                <span className="task-nav-index">{i + 1}</span>
                <span className="task-nav-id">{item.image_id}</span>
                <span className={`task-nav-state ${item.done ? 'done' : item.started ? 'started' : ''}`}>
                  {item.done ? 'done' : item.started ? 'in progress' : ''}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
